import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { DateFilter } from '@/components/DataTable/DateFilter'
import { useDateRange } from '@/components/DatePicker/hooks'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { isNova } from '@/config'
import usePermissions from '@/hooks/permissions/usePermissions'
import { useDefaultTerritories, useQueryDefaults } from '@/hooks/queries/defaults/useQueryDefaults'
import { orderSearchSchema, ordersExportQueryOptions, useOrders } from '@/hooks/queries/useOrders'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryClient } from '@/main'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { ALL_ORDERS_KEY } from '@repo/constants'
import { getNextWeekFriday } from '@repo/utils'
import { createFileRoute } from '@tanstack/react-router'
import { format, parse } from 'date-fns'
import { useTranslation } from 'react-i18next'
import NovaOrderDataTable from '../-components/NovaOrderDataTable'
import OrderDataTable from '../-components/OrderDataTable'
import { mainlineOrdersData, novaOrdersData } from '../-components/ordersListShape'
import { OrdersConfigMenu } from '../-components/OrdersConfigMenu'

export const Route = createFileRoute('/_auth/_modules/_app/orders/all/')({
  component: AllOrdersPage,
  validateSearch: (search) => orderSearchSchema.parse(search),
})

function AllOrdersPage() {
  const { t } = useTranslation('orders')
  const { canUser } = usePermissions()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const defaultTerritories = useDefaultTerritories()

  const searchKeys = Object.keys(search).sort()

  const isSearchOnlyOrderNum =
    (searchKeys.length === 1 && searchKeys[0] === 'orderNum') ||
    (searchKeys.length === 2 && searchKeys[0] === 'date' && searchKeys[1] === 'orderNum')

  useQueryDefaults({
    search,
    navigate,
    defaults: {
      territory: defaultTerritories,
      date: !isSearchOnlyOrderNum ? format(new Date(), 'yyyy-MM-dd') : undefined,
    },
  })

  const { isPending, error, data } = useOrders({
    ...search,
    flagged: false,
    page: search.page ?? 1,
    source: search.source,
    pageSize,
  })

  const { isDateInRange } = useDateRange({
    numberOfDaysBefore: 21,
    customEndDate: getNextWeekFriday(),
  })

  const onSelectDate = (date: Date | undefined) => {
    navigate({
      search: {
        ...search,
        date: date ? format(date, 'yyyy-MM-dd') : undefined,
        // Back to the first page, because the row the current page number refers to almost
        // certainly does not exist under the new date and the table would come back empty.
        page: 1,
      },
    })
  }

  const onSearch = (value: string) => {
    if (value) {
      navigate({ search: { ...search, keyword: value, page: 1 } })
    } else {
      const date = search.date ?? format(new Date(), 'yyyy-MM-dd')
      navigate({ search: { ...search, date, page: 1 } })
    }
  }

  const getCsvExport = () =>
    queryClient.ensureQueryData(ordersExportQueryOptions({ ...search, type: ALL_ORDERS_KEY }))

  const tableProps = {
    isPending,
    error,
    route: Route,
    pageSize,
    onPageSizeChange,
    onSearch,
  }

  return (
    <>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.ALL_ORDERS}
        title={t('tutorials.allOrders.dialog.title')}
        guideUrl="/orders/all-orders"
      >
        {t('tutorials.allOrders.dialog.description')}
      </IntroductionDialog>

      <PageContainer maxWidth="wide">
        <Spread>
          <PageHeader>{t('allOrdersPage.heading')}</PageHeader>
          <HStack gap="sm">
            {/* Nova only: everything it configures is served by Nova-only endpoints, so on a
                mainline environment the menu would open onto failing requests. Business Admins
                only, through the edit permission. */}
            {isNova && canUser('orders_leg_release_timeout_edit') && <OrdersConfigMenu />}

            <ExportCsvButton getCsvExport={getCsvExport} rowCount={+(data?.rowCount ?? 0)} />

            <DateFilter
              disabled={(date) => !isDateInRange(date)}
              defaultDate={search.date ? parse(search.date, 'yyyy-MM-dd', new Date()) : undefined}
              onSelect={onSelectDate}
            />
          </HStack>
        </Spread>

        <Card>
          <CardContent className="py-6">
            {/* Forked rather than gated: the Nova list reads nova_orders_view, so its rows carry
                coupling state, the customer PO and per-order delivery completeness that no mainline
                row has, and it filters on a column no mainline database has. The props are shared
                so the two tables cannot drift apart on anything that is not a real difference. */}
            {isNova ? (
              <NovaOrderDataTable {...tableProps} data={novaOrdersData(data)} />
            ) : (
              <OrderDataTable {...tableProps} data={mainlineOrdersData(data)} />
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </>
  )
}
